import { MULTIPART, adoptForm } from "../../../components/Esign/signing/adopt-form.ts"
import { jsonBody } from "../../../components/Esign/signing/bytes.ts"
import { fieldActions } from "../../../components/Esign/signing/field-actions.ts"
import { shareInFlight } from "../../../components/Esign/signing/in-flight.ts"
import {
  accepted,
  markOutcome,
  toOutcome
} from "../../../components/Esign/signing/outcome.ts"
import type {
  MarkKind,
  Outcome,
  SigningTransport
} from "../../../components/Esign/signing/types.ts"
import { request } from "../../../services/api"
import { saveBlob } from "../../../utils/files/save-blob.ts"
import type { CountersignState } from "../types/countersign.types.ts"

const base = (id: string) => `/esign/envelopes/${id}/countersign/`

const answer = <T>(response: { status?: number; data?: unknown }) =>
  toOutcome<T>(response.status ?? 0, response.data)

// A refusal on a bytes route is JSON inside the body.
const refusalOf = async <T>(response: {
  status?: number
  data?: unknown
}): Promise<Outcome<T>> =>
  toOutcome<T>(response.status ?? 0, await jsonBody(response.data))

export const getCountersignState = async (id: string) =>
  answer<CountersignState>(await request.api.get(base(id)))

export const agreeToCountersign = async (id: string, textSha256: string) =>
  answer<{ agreed: boolean }>(
    await request.api.post(`${base(id)}consent/`, { text_sha256: textSha256 })
  )

// `preloaded` is read on the first getState, so a disclosure view the page already recorded is not recorded twice.
export const createCountersignTransport = (
  id: string,
  preloaded?: () => CountersignState | null
): SigningTransport => {
  const post = async <T>(path: string, body: unknown) =>
    answer<T>(await request.api.post(`${base(id)}${path}`, body))

  const readDocument = async (): Promise<Outcome<Uint8Array>> => {
    const response = await request.api.get<ArrayBuffer>(
      `${base(id)}document/`,
      {},
      { responseType: "arraybuffer" }
    )
    if (!response.ok || !response.data) return refusalOf(response)
    return accepted(response.status ?? 200, new Uint8Array(response.data))
  }

  return {
    // Strict mode asks twice in one tick; both share the one answer.
    getState: shareInFlight(async () => {
      const held = preloaded?.() ?? null
      preloaded = undefined
      return held ? accepted(200, held) : getCountersignState(id)
    }),
    // Every read of the document is a recorded opening, and strict mode asks twice.
    getDocument: shareInFlight(readDocument),
    download: async (filename) => {
      const response = await request.api.get<Blob>(
        `${base(id)}document/`,
        { download: "true" },
        { responseType: "blob" }
      )
      if (!response.ok || !response.data) return refusalOf(response)
      saveBlob(response.data, filename)
      return accepted(response.status ?? 200, null)
    },
    adopt: async (input) =>
      answer(
        await request.api.post(`${base(id)}adopt/`, adoptForm(input), {
          headers: MULTIPART
        })
      ),
    getAdoptedMark: async (kind: MarkKind) => {
      const response = await request.api.get<Blob>(
        `${base(id)}adopt/${kind}/`,
        {},
        { responseType: "blob" }
      )
      return markOutcome(response.status ?? 0, response.data)
    },
    ...fieldActions(post),
    // The colleague is signed in, so the audit trail needs no view report.
    pageViewed: async () => undefined
  }
}
